import { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { VendorType } from '@econtractor/types';
import * as apiVendors from 'api/apiVendors';
import { useGlobal } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import ESpinner from 'components/others/Spinner';
import TitleCounter from 'components/others/TitleCounter';
import OffVendorEdit from './OffVendorEdit';
import OffVendorCustomers from './OffVendorCustomers';
import OffVendorPersons from './OffVendorPersons';
import VendorToolBar from './VendorToolBar';
import VendorsTable from './VendorsTable';
import { useVendors } from './context/VendorsProvider';

const Vendors = () => {
    const { t } = useTranslation(['Vendors', 'common']);
    const { g } = useGlobal();
    const { showToast } = useToast();
    const {
        showSpinner,
        setShowSpinner,
        vendorsOrder,
        vendorsDesc,
        setVendorsOrder,
        setVendorsDesc,
        listResetNonce,
        listRefreshNonce,
    } = useVendors();

    const [vendorsList, setVendorsList] = useState<VendorType[] | null>(null);
    const [page, setPage] = useState<number | null>(null);
    const [editVendorId, setEditVendorId] = useState<number | null>(null);
    const [subjectsVendorId, setSubjectsVendorId] = useState<number | null>(null);
    const [personsVendorId, setPersonsVendorId] = useState<number | null>(null);
    const location = useLocation();
    const onPage = useListOnPage();
    const prevListData = useRef<string | null>(null);
    const skipFetchOnStateChangeRef = useRef(false);

    const fetchVendors = async (force = false) => {
        if (force) prevListData.current = null;
        const search = `%${g?.search?.[location.pathname] || ''}%`;
        if (!page || showSpinner) return;

        const listData = {
            search,
            order: `${vendorsOrder ? `order by ${vendorsOrder} ` : ''}${vendorsDesc ? 'DESC' : ''}`,
            page: page < 0 ? 1 : page || 1,
            onPage,
        };

        if (prevListData.current === JSON.stringify(listData)) return;
        prevListData.current = JSON.stringify(listData);

        setShowSpinner(true);
        const resVendors = await apiVendors.getList(listData);
        setShowSpinner(false);

        if (!resVendors) return showToast(t('common:messError'), 'danger');

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: resVendors.paginator,
        }));

        setVendorsList(resVendors.vendors);
    };

    const resetVendorsList = useCallback(async () => {
        skipFetchOnStateChangeRef.current = true;
        prevListData.current = null;
        setVendorsOrder('');
        setVendorsDesc(false);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);

        const search = `%${g?.search?.[location.pathname] || ''}%`;
        const listData = {
            search,
            order: '',
            page: 1,
            onPage,
        };

        setShowSpinner(true);
        const resVendors = await apiVendors.getList(listData);
        setShowSpinner(false);

        if (!resVendors) {
            skipFetchOnStateChangeRef.current = false;
            return showToast(t('common:messError'), 'danger');
        }

        prevListData.current = JSON.stringify(listData);

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: resVendors.paginator,
        }));

        setVendorsList(resVendors.vendors);
        skipFetchOnStateChangeRef.current = false;
    }, [g, location.pathname, onPage, setVendorsDesc, setVendorsOrder, setShowSpinner, showToast, t]);

    useListRefreshEffect(listResetNonce, () => {
        void resetVendorsList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        prevListData.current = null;
        void fetchVendors(true);
    });

    useEffect(() => {
        if (page === 1) fetchVendors(true);
        else setPage(1);
    }, [g.search?.[location.pathname], g.target]);

    useEffect(() => {
        if (skipFetchOnStateChangeRef.current) return;
        fetchVendors();
    }, [page, vendorsOrder, vendorsDesc, onPage]);

    useEffect(() => {
        const _page = g?.paginator?.[location.pathname]?.currentPage || 1;
        if (page !== _page) setPage(_page);
    }, [
        g?.paginator?.[location.pathname]?.currentPage,
        g?.paginator?.[location.pathname]?.countRows,
        g?.paginator?.[location.pathname]?.onPage,
    ]);

    return (
        <>
            <OffVendorEdit vendorId={editVendorId} setVendorId={setEditVendorId} fetchVendors={fetchVendors} />
            <OffVendorCustomers
                vendorId={subjectsVendorId}
                vendor={vendorsList?.find((item) => item.ID === subjectsVendorId)}
                onHide={() => setSubjectsVendorId(null)}
            />
            <OffVendorPersons
                vendorId={personsVendorId}
                vendor={vendorsList?.find((item) => item.ID === personsVendorId)}
                onHide={() => setPersonsVendorId(null)}
            />

            <Row>
                <Col className="col-sticky" sm={12}>
                    <div className="w-100 d-sm-flex pt-1 justify-content-between align-items-start">
                        <div>
                            <h4 className="p-0 m-0 mb-0 me-2 text-break">{t('title')}</h4>
                            <TitleCounter />
                        </div>
                        <div className="mt-2 mt-sm-0">
                            <VendorToolBar setEditVendorId={setEditVendorId} />
                        </div>
                    </div>
                </Col>

                <Col sm={12}>
                    {vendorsList ? (
                        <VendorsTable
                            vendorsList={vendorsList}
                            fetchVendors={fetchVendors}
                            setEditVendorId={setEditVendorId}
                            setSubjectsVendorId={setSubjectsVendorId}
                            setPersonsVendorId={setPersonsVendorId}
                        />
                    ) : (
                        <ESpinner />
                    )}
                </Col>
            </Row>
        </>
    );
};

export default Vendors;
